import { Ban, CheckCircle2, RotateCcw, Ruler } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { UnitOfMeasureModal } from '../components/UnitOfMeasureModal';
import { unitsOfMeasureApi } from '../inventoryApi';
import type { UnitOfMeasureListItem } from '../inventoryTypes';

type TabKey = 'info';

/** One unit of measure. */
export function UnitOfMeasureProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [unit, setUnit] = useState<UnitOfMeasureListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      unitsOfMeasureApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;

          setUnit(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this unit.', 0));
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  async function restore() {
    if (!id) return;

    setRestoring(true);
    setError(null);

    try {
      await unitsOfMeasureApi.activate(id);
      load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not restore this unit.', 0));
    } finally {
      setRestoring(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Logistics',
    'Inventory',
    { label: 'Settings', to: '/inventory/settings' },
    { label: 'Units of Measure', to: '/inventory/settings/units' },
    unit?.name ?? 'Unit',
  ];

  if (error && !unit) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (loading || !unit) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const { itemCount } = unit;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="unit-of-measure-profile"
        header={
          <ProfileHeader
            id="unit-of-measure-profile-header"
            icon={<Ruler className="h-6 w-6" aria-hidden="true" />}
            title={unit.name}
            meta={[unit.code, `${itemCount} ${itemCount === 1 ? 'item is' : 'items are'} counted in it`]}
            status={
              <StatusText tone={unit.isActive ? 'active' : 'inactive'}>
                {unit.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={unit.createdByName}
            createdAt={unit.createdAt}
          />
        }
        banner={
          error ? (
            <ProfileBanner
              id="unit-of-measure-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={error.message}
            />
          ) : unit.isActive ? (
            <ProfileBanner
              id="unit-of-measure-profile-banner"
              tone="slate"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="This unit is offered on the item form. Quantities are summed by it, which is why it is a catalog rather than free text on the item — two spellings of one unit would produce stock figures nothing can add together."
            />
          ) : (
            <ProfileBanner
              id="unit-of-measure-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This unit is deleted. It is no longer offered, and the items already counted in it keep resolving its code."
              action={
                <PermissionGate permission="units-of-measure.delete">
                  <Button
                    id="unit-of-measure-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void restore()}
                    disabled={restoring}
                    loading={restoring}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="unit-of-measure-details"
          title="Details"
          menuItems={[
            {
              key: 'edit',
              label: 'Edit',
              permission: 'units-of-measure.update',
              onClick: () => setEditing(true),
            },
          ]}
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Code', value: unit.code, mono: true },
              { label: 'Name', value: unit.name },
              { label: 'Description', value: unit.description },
              {
                label: 'Items counted in it',
                value: (
                  <span className={`font-mono tabular-nums ${itemCount === 0 ? 'text-slate-300' : ''}`}>
                    {itemCount}
                  </span>
                ),
              },
              { label: 'Order', value: <span className="font-mono tabular-nums">{unit.sortOrder}</span> },
            ]}
          />
        </ProfileCard>

        {/* Audit is never editable: created and updated are a record of what happened. */}
        <ProfileCard id="unit-of-measure-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: unit.createdByName },
              {
                label: 'Created',
                value: (
                  <time dateTime={toIso(unit.createdAt)} className="font-mono">
                    {formatDateTime(unit.createdAt)}
                  </time>
                ),
              },
              { label: 'Updated by', value: unit.updatedByName },
              {
                label: 'Updated',
                value: unit.updatedAt ? (
                  <time dateTime={toIso(unit.updatedAt)} className="font-mono">
                    {formatDateTime(unit.updatedAt)}
                  </time>
                ) : null,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <UnitOfMeasureModal
          open
          editing={unit}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
